import React from "react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalRecords?: string | number;
  onPageChange: (page: number) => void;
}

const PaginationComp: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const showEllipsisStart = currentPage > 4;
    const showEllipsisEnd = currentPage < totalPages - 3;

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    pages.push(1);
    if (showEllipsisStart) {
      pages.push("...");
    }

    const middleStart = Math.max(2, currentPage - 1);
    const middleEnd = Math.min(totalPages - 1, currentPage + 1);

    for (let i = middleStart; i <= middleEnd; i++) {
      if (!showEllipsisStart || i > 2) {
        if (!showEllipsisEnd || i < totalPages - 1) {
          pages.push(i);
        }
      }
    }

    if (showEllipsisEnd) {
      pages.push("...");
    }
    pages.push(totalPages);

    return pages;
  };

  return (
    <div className="flex justify-center py-4 w-full">
      <nav
        className="inline-flex items-center space-x-1"
        aria-label="Pagination"
      >
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className={`p-2 text-base rounded-md ${
            currentPage === 1
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray-700 hover:text-[#0eb683] hover:bg-gray-100"
          }`}
          aria-label="First page"
        >
          {"<<"}
        </button>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`p-2 text-base rounded-md ${
            currentPage === 1
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray-700 hover:text-[#0eb683] hover:bg-gray-100"
          }`}
          aria-label="Previous page"
        >
          {"<"}
        </button>

        {getPageNumbers().map((pageNum, index) => (
          <button
            key={index}
            onClick={() =>
              typeof pageNum === "number" ? onPageChange(pageNum) : undefined
            }
            disabled={pageNum === "..."}
            className={`px-3 py-1 text-sm rounded-md ${
              pageNum === currentPage
                ? "bg-[#0eb683] text-white"
                : pageNum === "..."
                ? "text-gray-700"
                : "text-gray-700 hover:text-[#0eb683] hover:bg-gray-100"
            }`}
          >
            {pageNum}
          </button>
        ))}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`p-2 text-base rounded-md ${
            currentPage === totalPages
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray-700 hover:text-[#0eb683] hover:bg-gray-100"
          }`}
          aria-label="Next page"
        >
          {">"}
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className={`p-2 text-base rounded-md ${
            currentPage === totalPages
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray-700 hover:text-[#0eb683] hover:bg-gray-100"
          }`}
          aria-label="Last page"
        >
          {">>"}
        </button>
      </nav>
    </div>
  );
};

export default PaginationComp;
